/**
 * Phase K Pass 1 — Tab strip for the admin dashboard.
 *
 * Visible inline at lg+ viewports (all 7 tabs); below lg the active tab
 * shows alone next to a "More ▾" dropdown that lists every tab.
 */
import React, { useEffect, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function useIsNarrow(breakpoint = 1024) {
    const [narrow, setNarrow] = useState(
        typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
    );
    useEffect(() => {
        const onResize = () => setNarrow(window.innerWidth < breakpoint);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return narrow;
}

export function AdminDashboardTabs({ tabs, activeTabId, onChange }) {
    const isNarrow = useIsNarrow(1024);
    const active = tabs.find((t) => t.id === activeTabId) || tabs[0];

    if (isNarrow) {
        // < lg: show active tab + More ▾ dropdown with full list.
        return (
            <div
                data-testid="admin-dashboard-tabs-narrow"
                className="flex items-center justify-between gap-3 border-b border-slate-200"
            >
                <div className="px-3 py-2.5 text-sm font-semibold text-slate-900 inline-flex items-center gap-2 border-b-2 border-blue-900 -mb-px">
                    <span data-testid="admin-dashboard-tabs-active-label">{active.label}</span>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            data-testid="admin-dashboard-tabs-more-trigger"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-100 transition-colors"
                        >
                            More <ChevronDown size={14} className="opacity-70" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="min-w-[240px]"
                        data-testid="admin-dashboard-tabs-more-menu"
                    >
                        {tabs.map((t) => (
                            <DropdownMenuItem
                                key={t.id}
                                data-testid={`admin-dashboard-tabs-more-item-${t.id}`}
                                onSelect={() => onChange(t.id)}
                                className="gap-2"
                            >
                                <Check
                                    size={14}
                                    className={
                                        t.id === activeTabId
                                            ? "text-blue-900 opacity-100"
                                            : "opacity-0"
                                    }
                                />
                                <span
                                    className={
                                        t.id === activeTabId
                                            ? "text-sm font-semibold text-slate-900"
                                            : "text-sm text-slate-700"
                                    }
                                >
                                    {t.label}
                                </span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        );
    }

    // lg+: render all 7 tabs inline.
    return (
        <div
            data-testid="admin-dashboard-tabs"
            // Phase O.1 — explicit ``overflow-y: hidden``. Without it, the
            // active tab's ``border-b-2 + -mb-px`` pulls the underline 1px
            // beyond the container; with the implicit ``overflow-y: auto``
            // that ``overflow-x-auto`` triggers (CSS spec collapses
            // ``visible`` to ``auto`` when the other axis is set), the
            // browser renders a stray vertical scrollbar + chevrons.
            // Horizontal stays ``auto`` for narrow-viewport tab wrap.
            className="border-b border-slate-200 -mx-1 overflow-x-auto overflow-y-hidden"
            role="tablist"
        >
            <div className="flex items-stretch gap-1 px-1">
                {tabs.map((t) => {
                    const isActive = t.id === activeTabId;
                    return (
                        <button
                            type="button"
                            key={t.id}
                            role="tab"
                            aria-selected={isActive}
                            data-testid={`admin-dashboard-tab-${t.id}`}
                            onClick={() => onChange(t.id)}
                            className={
                                "relative px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors outline-none " +
                                (isActive
                                    ? "text-slate-900 border-b-2 border-blue-900 -mb-px"
                                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md")
                            }
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default AdminDashboardTabs;
